import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button, Input, PasswordInput, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, Card, LongText, shortName } from '../../../shared/components';
import { EditIcon, TrashIcon, ResetIcon, EyeIcon, EyeOffIcon, PlusIcon, DashboardIcon } from '../../../shared/icons';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { ApplicationMember, PersonLookup } from '../../../shared/services/settings/types';
import { CredentialImportResult, Employee } from '../../../shared/services/turnoGeo/types';
import { ApiError } from '../../../shared/services/http';
import { LccRole } from '../../../shared/auth/types';
import { AssignModulesModal } from '../../settings/components/AssignModulesModal';
import { TURNOGEO_ASSIGNABLE_MODULES, TURNOGEO_ROLE_DEFAULT_MODULES, TURNOGEO_LEADER_ONLY_MODULE_IDS } from '../../../shared/config/turnoGeoModules';
import { InfoTip } from './InfoTip';
import { EmployeeFichaModal } from './EmployeeFichaModal';

const APP_CODE = 'turno-geo';
const settingsRepo = getSettingsRepository();
const tgRepo = getTurnoGeoRepository();

const ROLE_LABEL: Record<LccRole, string> = { operator: 'Empleado', supervisor: 'Supervisor', admin: 'Administrador' };
const ROLE_OPTIONS = (Object.keys(ROLE_LABEL) as LccRole[]).map((value) => ({ value, label: ROLE_LABEL[value] }));
const ROLE_HELP: Record<LccRole, string> = {
  operator: 'Empleado: marca entrada, almuerzo y salida, ve sus propios registros y radica novedades (incapacidad, vacaciones, permiso). No ve datos de otras personas.',
  supervisor: 'Supervisor: todo lo del empleado y además aprueba o rechaza las novedades del equipo (nunca las propias).',
  admin: 'Administrador: todo lo anterior y toda la Configuración: usuarios, claves, parámetros, ubicaciones, correcciones de marcaciones, inconsistencias y carga de empleados.',
};

const emptyForm = { fullName: '', email: '', password: '', role: 'operator' as LccRole };
const randomPassword = () => `Colombia2026${Array.from(crypto.getRandomValues(new Uint32Array(4)), (n) => n % 10).join('')}`;
const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Tab = 'agregar' | 'usuarios';

/** Texto secreto con ojito: se pide al servidor solo cuando se pulsa, y se vuelve a ocultar solo. */
function RevealSecret({ load, empty = '—' }: { load: () => Promise<string | null>; empty?: string }) {
  const toast = useToast();
  const [value, setValue] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const toggle = async () => {
    if (value !== undefined) { setValue(undefined); return; }
    setBusy(true);
    try {
      setValue(await load());
      timer.current = window.setTimeout(() => setValue(undefined), 20000);
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudo leer la clave.'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="break-all font-mono text-xs">{value === undefined ? '••••••••••••' : value ?? empty}</span>
      <button type="button" onClick={toggle} disabled={busy} aria-label={value === undefined ? 'Mostrar' : 'Ocultar'} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-50">
        {value === undefined ? <EyeIcon width={16} height={16} /> : <EyeOffIcon width={16} height={16} />}
      </button>
    </span>
  );
}

export function AccessAdminTab() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('agregar');
  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [employees, setEmployees] = useState<Map<string, Employee>>(new Map());
  const [credIds, setCredIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      settingsRepo.listApplicationMembers(APP_CODE, true),
      tgRepo.listEmployees().catch(() => [] as Employee[]),
      tgRepo.listCredentialUserIds().catch(() => [] as string[]),
    ]).then(([m, e, c]) => {
      setMembers(m);
      setEmployees(new Map(e.map((x) => [x.userId, x])));
      setCredIds(new Set(c));
      setIsLoading(false);
    });
  }, []);
  useEffect(load, [load]);

  // ---------------- Agregar ----------------
  const [form, setForm] = useState(emptyForm);
  const [lookup, setLookup] = useState<PersonLookup['person'] | null>(null);
  const [lookedUp, setLookedUp] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<typeof emptyForm>) => setForm((f) => ({ ...f, ...patch }));

  const checkEmail = async () => {
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email) || email === lookedUp) return;
    setLookedUp(email);
    try {
      const r = await settingsRepo.lookupPersonByEmail(APP_CODE, email);
      setLookup(r.found ? r.person ?? null : null);
    } catch {
      setLookup(null);
    }
  };

  const emailError = form.email && !EMAIL_RE.test(form.email.trim()) ? 'Revisa el correo: debe verse como nombre@dominio.com' : undefined;

  const create = async () => {
    if (!form.fullName.trim() || !EMAIL_RE.test(form.email.trim()) || form.password.length < 8) {
      toast.show('Completa nombre, un correo válido y una clave de mínimo 8 caracteres.', 'error');
      return;
    }
    setSaving(true);
    try {
      const created = await settingsRepo.createUser({ fullName: form.fullName.trim(), email: form.email.trim(), password: form.password, role: form.role });
      let kept = true;
      try { await tgRepo.setInitialPassword(created.id, form.password); } catch { kept = false; }
      toast.show(kept ? 'Usuario creado. Ya puede iniciar sesión con ese correo y clave.' : 'Usuario creado, pero su clave no quedó guardada para consulta (falta configurar la llave de claves en el servidor).');
      setForm(emptyForm); setLookup(null); setLookedUp('');
      load();
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudo crear el usuario.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const grantExisting = async () => {
    if (!lookup) return;
    setSaving(true);
    try {
      await settingsRepo.grantApplicationAccess(APP_CODE, lookup.id, form.role, null);
      toast.show(`${lookup.fullName} ahora tiene acceso a TurnoGeo con su clave actual.`);
      setForm(emptyForm); setLookup(null); setLookedUp('');
      load();
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudo dar el acceso.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Cargar claves desde Excel ----------------
  const fileRef = useRef<HTMLInputElement>(null);
  const [importResults, setImportResults] = useState<CredentialImportResult[] | null>(null);
  const [importing, setImporting] = useState(false);
  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setImporting(true);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = wb.SheetNames.find((n) => /clave/i.test(n)) ?? wb.SheetNames[0];
      const matrix = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], { header: 1, raw: true, defval: null, blankrows: false });
      const norm = (h: unknown) => String(h ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
      // Se busca la fila de encabezado (el Excel de resultados puede traer filas antes).
      const headerRow = matrix.findIndex((r) => (r as unknown[]).some((c) => /CORREO|EMAIL/.test(norm(c))) && (r as unknown[]).some((c) => /CLAVE|CONTRASENA|PASSWORD/.test(norm(c))));
      if (headerRow < 0) throw new Error('No encontré las columnas de correo y clave. El archivo debe tener un encabezado con EMAIL (o CORREO) y CLAVE.');
      const headers = (matrix[headerRow] as unknown[]).map(norm);
      const iMail = headers.findIndex((h) => /CORREO|EMAIL/.test(h));
      const iPass = headers.findIndex((h) => /CLAVE|CONTRASENA|PASSWORD/.test(h));
      const rows = matrix.slice(headerRow + 1).map((r) => ({ email: String((r as unknown[])[iMail] ?? '').trim(), password: String((r as unknown[])[iPass] ?? '').trim() })).filter((r) => EMAIL_RE.test(r.email) && r.password);
      if (rows.length === 0) throw new Error('El archivo no trae filas con correo y clave.');
      const all: CredentialImportResult[] = [];
      for (let i = 0; i < rows.length; i += 50) all.push(...(await tgRepo.importInitialPasswords(rows.slice(i, i + 50))));
      setImportResults(all);
      toast.show(`${all.filter((r) => r.status === 'GUARDADA').length} claves guardadas.`);
      load();
    } catch (err) {
      toast.show(errorMessage(err, err instanceof Error ? err.message : 'No se pudo leer el archivo.'), 'error');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ---------------- Usuarios ----------------
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [keyFilter, setKeyFilter] = useState('');
  const [ficha, setFicha] = useState<ApplicationMember | null>(null);
  const [revoking, setRevoking] = useState<ApplicationMember | null>(null);
  const [assigning, setAssigning] = useState<ApplicationMember | null>(null);
  const [editing, setEditing] = useState<ApplicationMember | null>(null);
  const [editForm, setEditForm] = useState({ fullName: '', email: '' });
  const [resetting, setResetting] = useState<ApplicationMember | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) =>
      (!q || m.fullName.toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q)) &&
      (!roleFilter || m.role === roleFilter) &&
      (!stateFilter || (stateFilter === 'activo' ? m.isActive : !m.isActive)) &&
      (!keyFilter || (keyFilter === 'con' ? credIds.has(m.id) : !credIds.has(m.id)))
    );
  }, [members, search, roleFilter, stateFilter, keyFilter, credIds]);

  const changeRole = async (m: ApplicationMember, role: LccRole) => {
    try {
      await settingsRepo.grantApplicationAccess(APP_CODE, m.id, role, m.allowedModules);
      toast.show(`Rol de ${m.fullName} actualizado.`);
      load();
    } catch (err) { toast.show(errorMessage(err, 'No se pudo cambiar el rol.'), 'error'); }
  };
  const saveModules = async (id: string, allowedModules: string[] | null) => {
    const m = members.find((x) => x.id === id);
    await settingsRepo.grantApplicationAccess(APP_CODE, id, m?.role ?? 'operator', allowedModules);
    toast.show('Pantallas actualizadas.');
    load();
  };
  const saveEdit = async () => {
    if (!editing) return;
    const patch: { fullName?: string; email?: string } = {};
    if (editForm.fullName.trim() && editForm.fullName.trim().toUpperCase() !== editing.fullName.toUpperCase()) patch.fullName = editForm.fullName.trim();
    if (editForm.email.trim().toLowerCase() !== (editing.email ?? '').toLowerCase()) patch.email = editForm.email.trim();
    if (!patch.fullName && !patch.email) { setEditing(null); return; }
    setBusy(true);
    try {
      await settingsRepo.updateUserProfile(editing.id, patch);
      toast.show('Datos actualizados.');
      setEditing(null);
      load();
    } catch (err) { toast.show(errorMessage(err, 'No se pudieron actualizar los datos.'), 'error'); } finally { setBusy(false); }
  };
  const savePassword = async () => {
    if (!resetting) return;
    if (newPassword.length < 8) { toast.show('La clave debe tener mínimo 8 caracteres.', 'error'); return; }
    setBusy(true);
    try {
      await settingsRepo.resetUserPassword(resetting.id, newPassword);
      let kept = true;
      try { await tgRepo.setInitialPassword(resetting.id, newPassword); } catch { kept = false; }
      toast.show(kept ? `Clave de ${resetting.fullName} actualizada y guardada.` : `Clave de ${resetting.fullName} actualizada (no quedó guardada para consulta).`);
      setResetting(null); setNewPassword('');
      load();
    } catch (err) { toast.show(errorMessage(err, 'No se pudo restablecer la clave.'), 'error'); } finally { setBusy(false); }
  };

  const columns: Column<ApplicationMember>[] = [
    {
      key: 'name', filterValue: (m) => m.fullName,
      header: 'Nombre',
      sortable: true,
      sortValue: (m) => m.fullName,
      render: (m) => (
        <button type="button" onClick={() => setFicha(m)} className="text-left font-semibold text-brand-700 hover:underline" title={`Ver ficha de ${m.fullName}`}>
          {shortName(m.fullName)}
        </button>
      ),
    },
    { key: 'email', filterValue: (m) => m.email ?? '', header: 'Correo (usuario)', render: (m) => <span className="break-all text-ink-500">{m.email ?? '—'}</span> },
    {
      key: 'role', filterValue: (m) => m.role,
      header: 'Rol',
      render: (m) => <Select options={ROLE_OPTIONS} value={m.role} onChange={(e) => changeRole(m, e.target.value as LccRole)} className="h-9 min-w-[150px] text-xs" />,
    },
    {
      key: 'modules',
      header: 'Pantallas',
      render: (m) => (
        <button onClick={() => setAssigning(m)} className="flex items-center gap-1.5 text-left text-xs font-medium text-brand-600 hover:underline">
          <DashboardIcon width={14} height={14} className="shrink-0" />
          <span>{m.allowedModules && m.allowedModules.length > 0 ? m.allowedModules.map((id) => TURNOGEO_ASSIGNABLE_MODULES.find((x) => x.id === id)?.label ?? id).join(', ') : 'Por defecto del rol'}</span>
        </button>
      ),
    },
    {
      key: 'key',
      header: 'Clave inicial',
      render: (m) => (credIds.has(m.id) ? <RevealSecret load={() => tgRepo.getInitialPassword(m.id)} /> : <span className="text-xs text-ink-300">Sin clave guardada</span>),
    },
    { key: 'state', filterValue: (m) => m.isActive ? 'Activo' : 'Inactivo', header: 'Estado', render: (m) => <Badge tone={m.isActive ? 'success' : 'neutral'}>{m.isActive ? 'Activo' : 'Inactivo'}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (m) => (
        <div className="flex items-center gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar nombre y correo" onClick={() => { setEditForm({ fullName: m.fullName, email: m.email ?? '' }); setEditing(m); }} className="h-9 w-9" />
          <IconButton icon={<ResetIcon width={16} height={16} />} label="Restablecer clave" onClick={() => { setNewPassword(''); setResetting(m); }} className="h-9 w-9" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Quitar acceso" onClick={() => setRevoking(m)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  const emp = ficha ? employees.get(ficha.id) : undefined;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 rounded-2xl bg-ink-50 p-1">
        {([['agregar', 'Agregar usuario'], ['usuarios', `Usuarios (${members.length})`]] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={['flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors', tab === id ? 'bg-surface text-brand-700 shadow-card' : 'text-ink-500'].join(' ')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'agregar' && (
        <div className="space-y-4">
          <Card>
            <div className="space-y-4">
              <p className="text-sm text-ink-500">Crea la cuenta de acceso y le da de una vez acceso a TurnoGeo. Lo que escribes se ve completo para que verifiques antes de crear.</p>
              <Input label="Nombres y apellidos" placeholder="Ej: María Fernanda Gómez Ruiz" value={form.fullName} onChange={(e) => set({ fullName: e.target.value })} />
              <div>
                <Input label="Correo (es el usuario con el que inicia sesión)" type="email" placeholder="nombre@dominio.com" value={form.email} error={emailError}
                  onChange={(e) => { set({ email: e.target.value }); setLookup(null); setLookedUp(''); }} onBlur={checkEmail} />
              </div>
              {lookup && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-ink-800">
                  {lookup.hasAccessHere ? (
                    <p>Ese correo ya pertenece a <strong>{lookup.fullName}</strong> y <strong>ya tiene acceso a TurnoGeo</strong> como {lookup.role ? ROLE_LABEL[lookup.role] : 'usuario'}. No hay nada que crear: búscalo en la pestaña Usuarios.</p>
                  ) : (
                    <>
                      <p>Ese correo ya existe en la plataforma: <strong>{lookup.fullName}</strong>{lookup.applications.length > 0 ? ` (tiene acceso a ${lookup.applications.join(', ')})` : ''}. No se crea otra cuenta: entra con la clave que ya tiene. La clave actual no se puede mostrar (queda cifrada en el proveedor de acceso); si la olvidó, se restablece desde Usuarios.</p>
                      <Button className="mt-2" size="md" onClick={grantExisting} isLoading={saving}>Dar acceso a TurnoGeo con su clave actual</Button>
                    </>
                  )}
                </div>
              )}
              {!lookup && (
                <div>
                  <PasswordInput label="Clave inicial (mínimo 8 caracteres)" placeholder="Escribe o genera una clave" value={form.password} onChange={(e) => set({ password: e.target.value })} />
                  <button type="button" onClick={() => set({ password: randomPassword() })} className="mt-1.5 text-xs font-semibold text-brand-600 hover:underline">Generar clave (Colombia2026 + 4 dígitos)</button>
                </div>
              )}
              <div>
                <Select label="Rol en TurnoGeo" options={ROLE_OPTIONS} value={form.role} onChange={(e) => set({ role: e.target.value as LccRole })} />
                <p className="mt-2 flex items-start gap-2 rounded-xl bg-ink-50 p-3 text-xs text-ink-600"><InfoTip text={ROLE_HELP[form.role]} title={ROLE_LABEL[form.role]} /><span>{ROLE_HELP[form.role]}</span></p>
              </div>
            </div>
          </Card>

          {!lookup && (
            <Card>
              <p className="text-sm font-semibold text-ink-800">Así quedará</p>
              <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                <div><dt className="text-[11px] font-semibold uppercase text-ink-400">Nombre</dt><dd className="break-words text-ink-800">{form.fullName || '—'}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase text-ink-400">Usuario (correo)</dt><dd className="break-all text-ink-800">{form.email || '—'}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase text-ink-400">Clave inicial</dt><dd className="break-all font-mono text-ink-800">{form.password || '—'}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase text-ink-400">Rol</dt><dd className="text-ink-800">{ROLE_LABEL[form.role]}</dd></div>
              </dl>
              <Button className="mt-4" fullWidth size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={create} isLoading={saving}>Crear usuario</Button>
            </Card>
          )}

          <Card>
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">Cargar claves desde Excel <InfoTip title="¿Para qué sirve?" text="Si ya entregaste claves a tu gente, sube el Excel (columnas EMAIL y CLAVE) y quedan guardadas cifradas para consultarlas con el ojito en la pestaña Usuarios. No cambia las claves reales de nadie." /></p>
              <p className="text-xs text-ink-500">Sirve el Excel de resultados de la carga masiva (hoja "Claves iniciales") o cualquier archivo con columnas EMAIL (o CORREO) y CLAVE. Las claves se guardan cifradas.</p>
              <Button variant="outline" size="md" onClick={() => fileRef.current?.click()} isLoading={importing}>Subir Excel de claves</Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
              {importResults && (
                <div className="space-y-1 text-xs">
                  <div className="flex flex-wrap gap-2">
                    <Badge tone="success">Guardadas: {importResults.filter((r) => r.status === 'GUARDADA').length}</Badge>
                    <Badge tone={importResults.some((r) => r.status === 'SIN_USUARIO') ? 'warning' : 'neutral'}>Sin usuario: {importResults.filter((r) => r.status === 'SIN_USUARIO').length}</Badge>
                    <Badge tone={importResults.some((r) => r.status === 'ERROR') ? 'critical' : 'neutral'}>Con error: {importResults.filter((r) => r.status === 'ERROR').length}</Badge>
                  </div>
                  {importResults.filter((r) => r.status !== 'GUARDADA').slice(0, 20).map((r, i) => <p key={i} className="text-ink-500"><span className="break-all font-medium">{r.email || '(sin correo)'}</span>: {r.message}</p>)}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 'usuarios' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Input placeholder="Buscar por nombre o correo" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select placeholder="Todos los roles" options={ROLE_OPTIONS} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} />
            <Select placeholder="Todos los estados" options={[{ value: 'activo', label: 'Activos' }, { value: 'inactivo', label: 'Inactivos' }]} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} />
            <Select placeholder="Con o sin clave guardada" options={[{ value: 'con', label: 'Con clave guardada' }, { value: 'sin', label: 'Sin clave guardada' }]} value={keyFilter} onChange={(e) => setKeyFilter(e.target.value)} />
          </div>
          <p className="flex items-start gap-2 text-xs text-ink-500"><InfoTip title="Correo (usuario)" text="El correo es el usuario con el que la persona inicia sesión en la plataforma. Si lo cambias, deberá entrar con el nuevo." /><span>Aquí solo aparecen las personas con acceso a TurnoGeo. Pulsa el nombre para ver su ficha. La clave inicial es la que se entregó: si la persona la cambió, ya no coincide.</span></p>
          <DataTable excel fixedKey="name" columns={columns} rows={filtered} rowKey={(m) => m.id} isLoading={isLoading} defaultSortKey="name" defaultSortDir="asc" emptyTitle="Sin usuarios con esos filtros" />
        </div>
      )}

      {ficha && (
        <EmployeeFichaModal
          open
          onClose={() => setFicha(null)}
          userId={ficha.id}
          fullName={ficha.fullName}
          email={ficha.email ?? ""}
          employee={emp}
          onSaved={load}
          header={
            <div className="flex flex-wrap gap-2">
              <Badge tone="info">{ROLE_LABEL[ficha.role]}</Badge>
              <Badge tone={ficha.isActive ? 'success' : 'neutral'}>{ficha.isActive ? 'Activo' : 'Inactivo'}</Badge>
            </div>
          }
          extra={
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Clave inicial</p>
              <div className="text-sm text-ink-800">{credIds.has(ficha.id) ? <RevealSecret load={() => tgRepo.getInitialPassword(ficha.id)} /> : <span className="text-ink-300">Sin clave guardada</span>}</div>
            </div>
          }
        />
      )}

      <ConfirmDialog
        open={!!revoking}
        title="Quitar acceso"
        description={`¿Quitar el acceso de ${revoking?.fullName} a TurnoGeo? Su acceso a otras aplicaciones de la Suite no se ve afectado.`}
        tone="danger"
        confirmLabel="Quitar acceso"
        onCancel={() => setRevoking(null)}
        onConfirm={async () => {
          if (revoking) await settingsRepo.revokeApplicationAccess(APP_CODE, revoking.id);
          setRevoking(null);
          load();
        }}
      />
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Editar usuario">
        <div className="space-y-4">
          <Input label="Nombres y apellidos" value={editForm.fullName} onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))} />
          <Input label="Correo (usuario)" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
          <p className="text-xs text-ink-400">El cambio aplica a la persona en toda la Suite. Si cambias el correo, deberá iniciar sesión con el nuevo.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
            <Button fullWidth onClick={saveEdit} isLoading={busy}>Guardar</Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!resetting} onClose={() => setResetting(null)} title="Restablecer clave">
        <div className="space-y-4">
          <p className="text-sm text-ink-600">Usuario: <span className="font-semibold">{resetting?.fullName}</span></p>
          <PasswordInput label="Nueva clave (mínimo 8 caracteres)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <button type="button" onClick={() => setNewPassword(randomPassword())} className="text-xs font-semibold text-brand-600 hover:underline">Generar clave</button>
          <p className="text-xs text-ink-400">Reemplaza su clave de inmediato, cierra sus otras sesiones y queda guardada (cifrada) para consultarla aquí. Aplica a toda la Suite.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setResetting(null)}>Cancelar</Button>
            <Button fullWidth onClick={savePassword} isLoading={busy}>Restablecer</Button>
          </div>
        </div>
      </Modal>
      <AssignModulesModal user={assigning} onClose={() => setAssigning(null)} onSave={saveModules} assignableModules={assigning?.role === 'operator' ? TURNOGEO_ASSIGNABLE_MODULES.filter((m) => !(TURNOGEO_LEADER_ONLY_MODULE_IDS as readonly string[]).includes(m.id)) : TURNOGEO_ASSIGNABLE_MODULES} roleDefaultModules={TURNOGEO_ROLE_DEFAULT_MODULES} />
    </div>
  );
}
